import React, { useEffect, useRef, useState } from 'react';
import {
  Wifi,
  WifiOff,
  Battery,
  Signal,
  MapPin,
  Calendar,
  Wrench,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { iotApi, IoTNode } from '../lib/iotApi';
import {
  getNodeMetrics,
  getNodeState,
  getValveChannels,
  valveChannelOrdinal,
  type MetricKey,
  type NodeState,
} from '../lib/liveSensorMetrics';
import { useIntlFormatting } from '../utils/intl';
import { IS_AWS_MODE } from '../config/frontend';

interface Device {
  id: string;
  name: string;
  type: 'gateway' | 'valve_node' | 'motor_node' | 'sensor_node';
  model: string;
  serial: string;
  firmware: string;
  status: 'online' | 'offline' | 'maintenance' | 'warning';
  last_seen: string;
  location: string;
  zone?: string;
  battery_level?: number;
  signal_strength?: number;
  uptime?: string;
  data?: {
    temperature?: number;
    humidity?: number;
    pressure?: number;
    flow_rate?: number;
    valve_position?: number;
  };
}

// ---------------------------------------------------------------------------
// FIELD-LIVE-SENSOR-METRICS-0001 — farmer read-only Sensors page (AWS mode only)
// Model-aware live metrics derived from canonical measurements via the shared
// interpreter. Legacy scalar aliases are a compatibility fallback only.
// ---------------------------------------------------------------------------

const UNKNOWN = '—';

/** Freshest valid communication timestamp across a node's fields (epoch ms), or null. */
const nodeTimestampMs = (node: IoTNode): number | null => {
  const candidates = [node.last_seen_at, node.latest?.ts]
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .map((value) => Date.parse(value))
    .filter((ms) => Number.isFinite(ms));
  return candidates.length > 0 ? Math.max(...candidates) : null;
};

/** Friendly display text; missing/blank → UNKNOWN (never a fake value). */
const displayText = (raw: string | null | undefined): string =>
  typeof raw === 'string' && raw.trim().length > 0 ? raw : UNKNOWN;

interface DevicesProps {
  farmId?: string;
}

const Devices: React.FC<DevicesProps> = ({ farmId }) => {
  const { t } = useTranslation(['devices', 'digitalTwin']);
  const { formatNumber, formatDateTime } = useIntlFormatting();
  const [filter, setFilter] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [awsNodes, setAwsNodes] = useState<IoTNode[]>([]);
  const [loadError, setLoadError] = useState(false);
  const hasLoadedRef = useRef(false);

  // Canonical node-list read path. A farm-scoped caller must never issue an
  // unqualified /iot/nodes read; the generic (non-CGM) component keeps its exact
  // historical bare /iot/nodes route ONLY when no farmId was provided at all.
  // A provided-but-empty farmId still builds `/iot/nodes?farm_id=` so the backend
  // fail-closes rather than silently widening to an unfiltered read.
  const nodeListPath =
    farmId === undefined
      ? '/iot/nodes'
      : `/iot/nodes?farm_id=${encodeURIComponent(farmId)}`;

  useEffect(() => {
    if (!IS_AWS_MODE) return;
    let active = true;
    let timer: number | undefined;

    // Farm-scope transition: a changed node-list path must never render the
    // previous farm's data. Immediately invalidate the prior scoped display and
    // its "already loaded" latch so a failure on the new path fail-closes
    // (error state) rather than preserving the old farm's rows.
    setAwsNodes([]);
    hasLoadedRef.current = false;
    setLoadError(false);

    // Non-overlapping refresh: schedule the next load only after the previous
    // settles, so a slow request can never pile up concurrent node-list calls.
    const load = () => {
      iotApi
        .getJson<IoTNode[]>(nodeListPath)
        .then((nodes) => {
          if (!active) return;
          hasLoadedRef.current = true;
          setAwsNodes(nodes);
          setLoadError(false);
        })
        .catch(() => {
          if (!active) return;
          // A failed refresh must NOT erase previously-loaded valid data —
          // but only for the SAME scoped path. A fresh path has its latch reset
          // above, so its first failure surfaces the error state.
          if (!hasLoadedRef.current) setLoadError(true);
        })
        .finally(() => {
          if (active) timer = window.setTimeout(load, 30000);
        });
    };

    load();

    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [nodeListPath]);

  if (IS_AWS_MODE) {
    const getStateLabel = (state: NodeState): string => {
      if (state.kind === 'valve') {
        return state.value === 1 ? t('farmer.states.valveOpen') : t('farmer.states.valveClosed');
      }
      if (state.kind === 'pump') {
        return state.value === 1 ? t('farmer.states.pumpOn') : t('farmer.states.pumpOff');
      }
      return state.value === 1 ? t('farmer.states.motorOn') : t('farmer.states.motorOff');
    };

    const getFriendlyTypeLabel = (model: string | null | undefined): string => {
      switch (model) {
        case 'asvwf01':
        case 'valve_water_flow':
          return t('farmer.types.irrigationValve');
        case 'relay_4ch':
          return t('farmer.types.relayControl');
        case 'soil_air_ecth':
          return t('farmer.types.soilAirSensor');
        case 'pipe_pressure':
          return t('farmer.types.pressureSensor');
        default:
          return t('farmer.types.equipment');
      }
    };

    const getMetricLabel = (key: MetricKey): string => {
      switch (key) {
        case 'flow':
          return t('digitalTwin:telemetryLabels.flow');
        case 'pressure':
          return t('digitalTwin:telemetryLabels.pressure');
        case 'soilMoisture':
          return t('digitalTwin:telemetryLabels.soilMoisture');
        case 'soilEc':
          return t('digitalTwin:telemetryLabels.soilEc');
        case 'soilTemperature':
          return t('digitalTwin:telemetryLabels.soilTemperature');
        case 'airTemperature':
          return t('digitalTwin:telemetryLabels.airTemperature');
        case 'airHumidity':
          return t('digitalTwin:telemetryLabels.airHumidity');
        case 'battery':
          return t('digitalTwin:telemetryLabels.battery');
      }
    };

    const getMetricUnit = (key: MetricKey): string => {
      switch (key) {
        case 'flow':
          return t('farmer.units.flowLpm');
        case 'pressure':
          return t('farmer.units.bar');
        case 'soilMoisture':
          return t('farmer.units.percent');
        case 'soilEc':
          return t('farmer.units.ec');
        case 'soilTemperature':
          return t('farmer.units.celsius');
        case 'airTemperature':
          return t('farmer.units.celsius');
        case 'airHumidity':
          return t('farmer.units.percent');
        case 'battery':
          return t('farmer.units.volt');
      }
    };

    const deriveSensorDisplay = (node: IoTNode) => {
      const metricRows = getNodeMetrics(node).map((metric) => ({
        label: getMetricLabel(metric.key),
        valueText: formatNumber(metric.value, { maximumFractionDigits: 1 }),
        unitText: getMetricUnit(metric.key),
      }));
      // asvwf01: expose each known valve channel independently (valve_1..valve_4).
      const valveChannels = node.model === 'asvwf01'
        ? getValveChannels(node.latest?.measures)
            .map((channel) => ({ ordinal: valveChannelOrdinal(channel.channel), open: channel.open }))
            .filter((channel): channel is { ordinal: number; open: boolean } => channel.ordinal !== undefined)
        : [];
      const state = getNodeState(node);
      const stateText = state === undefined ? UNKNOWN : getStateLabel(state);
      const lastCommMs = nodeTimestampMs(node);
      const lastCommText = lastCommMs === null ? UNKNOWN : formatDateTime(lastCommMs);
      return { metricRows, valveChannels, stateText, lastCommText };
    };

    const valveChannelLabel = (ordinal: number): string =>
      `${t('digitalTwin:telemetryLabels.valve')} ${ordinal}`;

    const renderValveChannels = (
      node: IoTNode,
      index: number,
      prefix: string,
    ) => {
      const { valveChannels } = deriveSensorDisplay(node);
      if (valveChannels.length === 0) return null;
      return (
        <div className="space-y-1">
          {valveChannels.map((channel) => (
            <div key={channel.ordinal} className="flex justify-between gap-2" data-testid={`${prefix}-${index}-${channel.ordinal}`}>
              <span className="text-[var(--text-secondary)]">{valveChannelLabel(channel.ordinal)}</span>
              <span>{getStateLabel({ kind: 'valve', value: channel.open ? 1 : 0 })}</span>
            </div>
          ))}
        </div>
      );
    };

    const renderMetrics = (node: IoTNode, index: number) => {
      const { metricRows } = deriveSensorDisplay(node);
      if (metricRows.length === 0) return <span>{UNKNOWN}</span>;
      return (
        <div className="space-y-1">
          {metricRows.map((metric, metricIndex) => (
            <div key={metricIndex} className="flex items-baseline gap-1">
              <span className="text-[var(--text-secondary)]">{metric.label}</span>
              <span className="font-medium" data-testid={`sensor-metric-value-${index}-${metricIndex}`}>
                {metric.valueText}
              </span>
              <span className="text-[var(--text-tertiary)]" data-testid={`sensor-metric-unit-${index}-${metricIndex}`}>
                {metric.unitText}
              </span>
            </div>
          ))}
        </div>
      );
    };

    const renderMobileMetrics = (node: IoTNode, index: number) => {
      const { metricRows } = deriveSensorDisplay(node);
      if (metricRows.length === 0) {
        return (
          <div className="text-sm font-semibold text-[var(--text-primary)]" data-testid={`sensor-mobile-value-${index}`}>
            {UNKNOWN}
          </div>
        );
      }
      return (
        <div className="space-y-1">
          {metricRows.map((metric, metricIndex) => (
            <div key={metricIndex} className="text-sm text-[var(--text-primary)]">
              <span className="text-[var(--text-secondary)]">{metric.label}</span>{' '}
              <span className="font-semibold" data-testid={`sensor-mobile-metric-value-${index}-${metricIndex}`}>
                {metric.valueText}
              </span>{' '}
              <span className="text-xs text-[var(--text-tertiary)]" data-testid={`sensor-mobile-metric-unit-${index}-${metricIndex}`}>
                {metric.unitText}
              </span>
            </div>
          ))}
        </div>
      );
    };

    const renderSensorMobileCard = (node: IoTNode, index: number) => {
      const { stateText, lastCommText } = deriveSensorDisplay(node);
      return (
        <div key={index} className="card" data-testid={`sensor-mobile-card-${index}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-medium text-[var(--text-primary)] truncate" data-testid={`sensor-mobile-name-${index}`}>
                {displayText(node.name)}
              </h3>
              <p className="text-xs text-[var(--text-secondary)]" data-testid={`sensor-mobile-type-${index}`}>
                {getFriendlyTypeLabel(node.model)}
              </p>
            </div>
            <div className="text-right shrink-0">
              {renderMobileMetrics(node, index)}
            </div>
          </div>
          <dl className="mt-2 space-y-1 text-xs">
            <div className="flex justify-between gap-2">
              <dt className="text-[var(--text-secondary)]">{t('farmer.columns.sector')}</dt>
              <dd className="text-[var(--text-primary)]" data-testid={`sensor-mobile-sector-${index}`}>{displayText(node.sector_name)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-[var(--text-secondary)]">{t('farmer.columns.state')}</dt>
              <dd className="text-[var(--text-primary)]" data-testid={`sensor-mobile-state-${index}`}>
                {renderValveChannels(node, index, 'sensor-mobile-valve') ?? stateText}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-[var(--text-secondary)]">{t('farmer.columns.lastCommunication')}</dt>
              <dd className="text-[var(--text-primary)]" data-testid={`sensor-mobile-last-comm-${index}`}>{lastCommText}</dd>
            </div>
          </dl>
        </div>
      );
    };

    return (
      <div className="space-y-6" data-testid="farmer-sensors">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]" data-testid="sensors-heading">
            {t('farmer.title')}
          </h1>
          <p className="text-body text-[var(--text-secondary)] mt-1" data-testid="sensors-subtitle">
            {t('farmer.subtitle')}
          </p>
        </div>

        {loadError && (
          <div
            className="card border border-[var(--color-danger)] text-[var(--color-danger)]"
            data-testid="devices-error"
          >
            {t('farmer.loadError')}
          </div>
        )}

        {/* Desktop/tablet sensor table */}
        <div className="hidden md:block card overflow-x-auto p-0">
          <table className="w-full text-sm" data-testid="sensors-table">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.name')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.type')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.sector')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.value')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.state')}</th>
                <th className="px-3 py-2 text-left text-[var(--text-secondary)]">{t('farmer.columns.lastCommunication')}</th>
              </tr>
            </thead>
            <tbody>
              {awsNodes.map((node, index) => {
                const { stateText, lastCommText } = deriveSensorDisplay(node);

                return (
                  <tr key={index} className="border-t border-[var(--border-light)]" data-testid={`sensor-row-${index}`}>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`sensor-name-${index}`}>
                      {displayText(node.name)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`sensor-type-${index}`}>
                      {getFriendlyTypeLabel(node.model)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`sensor-sector-${index}`}>
                      {displayText(node.sector_name)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`sensor-value-${index}`}>
                      {renderMetrics(node, index)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`sensor-state-${index}`}>
                      {renderValveChannels(node, index, 'sensor-valve') ?? stateText}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-primary)]" data-testid={`sensor-last-comm-${index}`}>
                      {lastCommText}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked sensor cards (no horizontal table scroll) */}
        <div className="md:hidden space-y-3" data-testid="sensors-mobile-list">
          {awsNodes.map((node, index) => renderSensorMobileCard(node, index))}
        </div>

        {awsNodes.length === 0 && !loadError && (
          <div className="text-center py-12" data-testid="devices-empty">
            <p className="text-body text-[var(--text-secondary)]">{t('farmer.noSensors')}</p>
          </div>
        )}
      </div>
    );
  }

  const demoDevices: Device[] = [
    {
      id: 'gw-001',
      name: 'Gateway Vignes Central',
      type: 'gateway',
      model: 'AgriGW-Pro',
      serial: 'GW001-2024-001',
      firmware: '2.1.4',
      status: 'online',
      last_seen: '2024-01-15T14:30:00Z',
      location: 'Domaine Les Vignes',
      battery_level: 98,
      signal_strength: 95,
      uptime: '45d 12h 30m',
      data: {
        temperature: 22.5,
        humidity: 65
      }
    },
    {
      id: 'vn-001',
      name: 'Valve Zone A-1',
      type: 'valve_node',
      model: 'SmartValve-200',
      serial: 'VN001-2024-002',
      firmware: '1.8.2',
      status: 'online',
      last_seen: '2024-01-15T14:28:00Z',
      location: 'Field A North',
      zone: 'Zone A-1',
      battery_level: 87,
      signal_strength: 82,
      uptime: '32d 8h 15m',
      data: {
        pressure: 3.2,
        valve_position: 75
      }
    },
    {
      id: 'vn-002',
      name: 'Valve Zone A-2',
      type: 'valve_node',
      model: 'SmartValve-200',
      serial: 'VN002-2024-003',
      firmware: '1.8.2',
      status: 'warning',
      last_seen: '2024-01-15T14:15:00Z',
      location: 'Field A South',
      zone: 'Zone A-2',
      battery_level: 23,
      signal_strength: 45,
      uptime: '28d 4h 22m',
      data: {
        pressure: 2.1,
        valve_position: 0
      }
    },
    {
      id: 'mn-001',
      name: 'Pump Station Alpha',
      type: 'motor_node',
      model: 'PowerPump-1000',
      serial: 'MN001-2024-004',
      firmware: '3.2.1',
      status: 'offline',
      last_seen: '2024-01-15T12:45:00Z',
      location: 'Central Pump House',
      battery_level: 0,
      signal_strength: 0,
      uptime: '0d 0h 0m',
      data: {
        pressure: 0,
        flow_rate: 0
      }
    },
    {
      id: 'sn-001',
      name: 'Moisture Sensor A-1-01',
      type: 'sensor_node',
      model: 'SoilSense-Pro',
      serial: 'SN001-2024-005',
      firmware: '1.4.7',
      status: 'online',
      last_seen: '2024-01-15T14:29:00Z',
      location: 'Zone A-1 Center',
      zone: 'Zone A-1',
      battery_level: 76,
      signal_strength: 88,
      uptime: '18d 16h 45m',
      data: {
        temperature: 18.2,
        humidity: 68
      }
    },
    {
      id: 'sn-002',
      name: 'Moisture Sensor B-1-01',
      type: 'sensor_node',
      model: 'SoilSense-Pro',
      serial: 'SN002-2024-006',
      firmware: '1.4.6',
      status: 'maintenance',
      last_seen: '2024-01-15T09:30:00Z',
      location: 'Zone B-1 North',
      zone: 'Zone B-1',
      battery_level: 45,
      signal_strength: 0,
      uptime: '0d 0h 0m',
      data: {
        temperature: 0,
        humidity: 0
      }
    }
  ];

  const devices: Device[] = demoDevices;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'var(--color-success)';
      case 'warning': return 'var(--color-warning)';
      case 'offline': return 'var(--color-danger)';
      case 'maintenance': return 'var(--color-info)';
      default: return 'var(--text-secondary)';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'offline': return XCircle;
      case 'maintenance': return Wrench;
      default: return Clock;
    }
  };

  const getDeviceTypeLabel = (type: string) => {
    switch (type) {
      case 'gateway': return t('deviceTypes.gateway');
      case 'valve_node': return t('deviceTypes.valveNode');
      case 'motor_node': return t('deviceTypes.motorNode');
      case 'sensor_node': return t('deviceTypes.sensorNode');
      default: return type;
    }
  };

  const getDeviceTypeIcon = (type: string) => {
    switch (type) {
      case 'gateway': return '\u{1F4E1}';
      case 'valve_node': return '\u{1F4A7}';
      case 'motor_node': return '⚡';
      case 'sensor_node': return '\u{1F4CA}';
      default: return '\u{1F527}';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online': return t('status.online');
      case 'warning': return t('status.warning');
      case 'offline': return t('status.offline');
      case 'maintenance': return t('status.maintenance');
      default: return status;
    }
  };

  const getBatteryColor = (level: number) => {
    if (level > 50) return 'var(--color-success)';
    if (level > 20) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  const getSignalColor = (strength: number) => {
    if (strength > 70) return 'var(--color-success)';
    if (strength > 40) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  const filteredDevices = devices.filter(device => {
    const statusMatch = filter === 'all' || device.status === filter;
    const typeMatch = selectedType === 'all' || device.type === selectedType;
    return statusMatch && typeMatch;
  });

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const lastSeen = new Date(timestamp);
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return t('timeJustNow');
    if (diffMins < 60) return t('timeMinutesAgo', { count: diffMins });
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('timeHoursAgo', { count: diffHours });
    const diffDays = Math.floor(diffHours / 24);
    return t('timeDaysAgo', { count: diffDays });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">{t('title')}</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            {t('subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">{t('filters.type.label')}</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
              data-testid="device-type-filter"
            >
              <option value="all">{t('filters.type.all')}</option>
              <option value="gateway">{t('filters.type.gateway')}</option>
              <option value="valve_node">{t('filters.type.valveNodes')}</option>
              <option value="motor_node">{t('filters.type.motorNodes')}</option>
              <option value="sensor_node">{t('filters.type.sensorNodes')}</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">{t('filters.status.label')}</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
              data-testid="device-status-filter"
            >
              <option value="all">{t('filters.status.all')}</option>
              <option value="online">{t('filters.status.online')}</option>
              <option value="warning">{t('filters.status.warning')}</option>
              <option value="offline">{t('filters.status.offline')}</option>
              <option value="maintenance">{t('filters.status.maintenance')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: t('stats.totalDevices'), value: devices.length, color: 'var(--color-primary)' },
          { label: t('stats.online'), value: devices.filter(d => d.status === 'online').length, color: 'var(--color-success)' },
          { label: t('stats.warnings'), value: devices.filter(d => d.status === 'warning').length, color: 'var(--color-warning)' },
          { label: t('stats.offline'), value: devices.filter(d => d.status === 'offline').length, color: 'var(--color-danger)' }
        ].map((stat, index) => (
          <div key={index} className="card text-center">
            <div
              className="text-2xl font-bold mb-1"
              style={{ color: stat.color }}
            >
              {stat.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Device Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredDevices.map((device) => {
          const StatusIcon = getStatusIcon(device.status);

          return (
            <div
              key={device.id}
              className="card hover:shadow-lg transition-all"
              data-testid={`device-card-${device.id}`}
            >
              {/* Device Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">{getDeviceTypeIcon(device.type)}</div>
                  <div>
                    <h3 className="text-heading-4 text-[var(--text-primary)]">{device.name}</h3>
                    <p className="text-body-sm text-[var(--text-secondary)]">
                      {getDeviceTypeLabel(device.type)}
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      {device.model} • {device.serial}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-1">
                  <StatusIcon
                    className="w-5 h-5"
                    style={{ color: getStatusColor(device.status) }}
                  />
                  <span
                    className="px-2 py-1 text-xs font-medium rounded-full text-white capitalize"
                    style={{ backgroundColor: getStatusColor(device.status) }}
                  >
                    {getStatusLabel(device.status)}
                  </span>
                </div>
              </div>

              {/* Location & Zone */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-[var(--text-secondary)]" />
                  <span className="text-sm text-[var(--text-primary)]">{device.location}</span>
                </div>
                {device.zone && (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-[var(--color-primary)] rounded-sm flex items-center justify-center">
                      <span className="text-xs text-white font-bold">Z</span>
                    </div>
                    <span className="text-sm text-[var(--text-primary)]">{device.zone}</span>
                  </div>
                )}
              </div>

              {/* System Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-2 bg-[var(--gray-50)] rounded-lg">
                  <div className="text-sm font-semibold text-[var(--text-primary)]">
                    v{device.firmware}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)]">{t('properties.firmware')}</div>
                </div>

                <div className="text-center p-2 bg-[var(--gray-50)] rounded-lg">
                  <div className="text-sm font-semibold text-[var(--text-primary)]">
                    {device.uptime || '0d 0h 0m'}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)]">{t('properties.uptime')}</div>
                </div>
              </div>

              {/* Battery & Signal */}
              {device.status !== 'offline' && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Battery
                      className="w-4 h-4"
                      style={{ color: getBatteryColor(device.battery_level || 0) }}
                    />
                    <div className="flex-1">
                      <div className="text-xs text-[var(--text-secondary)]">{t('properties.battery')}</div>
                      <div
                        className="text-sm font-medium"
                        style={{ color: getBatteryColor(device.battery_level || 0) }}
                      >
                        {device.battery_level || 0}%
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Signal
                      className="w-4 h-4"
                      style={{ color: getSignalColor(device.signal_strength || 0) }}
                    />
                    <div className="flex-1">
                      <div className="text-xs text-[var(--text-secondary)]">{t('properties.signal')}</div>
                      <div
                        className="text-sm font-medium"
                        style={{ color: getSignalColor(device.signal_strength || 0) }}
                      >
                        {device.signal_strength || 0}%
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sensor Data */}
              {device.data && Object.keys(device.data).length > 0 && (
                <div className="mb-4 p-3 bg-[var(--gray-50)] rounded-lg">
                  <div className="text-xs font-medium text-[var(--text-primary)] mb-2">{t('readings.title')}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {device.data.temperature && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('readings.temperature')}</span>
                        <span className="font-medium">{device.data.temperature}°C</span>
                      </div>
                    )}
                    {device.data.humidity && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('readings.humidity')}</span>
                        <span className="font-medium">{device.data.humidity}%</span>
                      </div>
                    )}
                    {device.data.pressure && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('readings.pressure')}</span>
                        <span className="font-medium">{device.data.pressure} bar</span>
                      </div>
                    )}
                    {device.data.flow_rate && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('readings.flow')}</span>
                        <span className="font-medium">{device.data.flow_rate} m³/h</span>
                      </div>
                    )}
                    {device.data.valve_position !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">{t('readings.valve')}</span>
                        <span className="font-medium">{device.data.valve_position}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Last Seen */}
              <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{getTimeAgo(device.last_seen)}</span>
                </div>

                <button
                  className="text-[var(--color-primary)] hover:text-[var(--color-primary-700)] font-medium"
                  data-testid={`device-details-${device.id}`}
                >
                  {t('deviceDetails')}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredDevices.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">{'\u{1F4F1}'}</div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">
            {t('noDevicesFound')}
          </h3>
          <p className="text-body text-[var(--text-secondary)]">
            {t('noDevicesMessage')}
          </p>
        </div>
      )}
    </div>
  );
};

export default Devices;
